import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  TableModule,
  TableConfiguration,
} from '@lifeomic/chroma-react/components/TableModule';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { MapPin, Video2 } from '@lifeomic/chromicons';
import { findCoding, formatCodingToken } from '@lifeomic/fhir-constants';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { fhirSystems } from '../fhir-systems';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fhirClient, useDataset } from '../../../../util/api';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { usePractitionerNames } from '../../../../util/hooks/usePractitionerNames';

const messages = defineMessages({
  type: {
    id: 'MemberAppointments.type',
    defaultMessage: 'Type',
  },
  startDate: {
    id: 'MemberAppointments.startDate',
    defaultMessage: 'Start Date',
  },
  with: {
    id: 'MemberAppointments.with',
    defaultMessage: 'With',
  },
  location: {
    id: 'MemberAppointments.location',
    defaultMessage: 'Location',
  },
  status: {
    id: 'MemberAppointments.status',
    defaultMessage: 'Status',
  },
  booked: {
    id: 'MemberAppointments.booked',
    defaultMessage: 'Booked',
  },
  arrived: {
    id: 'MemberAppointments.arrived',
    defaultMessage: 'Arrived',
  },
  fulfilled: {
    id: 'MemberAppointments.fulfilled',
    defaultMessage: 'Fulfilled',
  },
  cancelled: {
    id: 'MemberAppointments.cancelled',
    defaultMessage: 'Cancelled',
  },
  noshow: {
    id: 'MemberAppointments.noshow',
    defaultMessage: 'No Show',
  },
  pending: {
    id: 'MemberAppointments.pending',
    defaultMessage: 'Pending',
  },
  proposed: {
    id: 'MemberAppointments.proposed',
    defaultMessage: 'Proposed',
  },
});

const useStyles = makeStyles(theme => ({
  locationIcon: {
    color: theme.palette.text.hint,
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
  centerAlign: {
    textAlign: 'center',
  },
  tableScene: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
  },
  tableContainer: {
    flex: 1,
    maxHeight: 450,
    maxWidth: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'auto',
    width: '100%',
  },
}));

const getPractitionerParticipantId = (
  appointment: WithPlatformDefaults<fhir3.Appointment>,
): string | null => {
  const practitionerParticipant = appointment.participant?.find(participant =>
    participant.actor?.reference?.startsWith('Practitioner/'),
  );
  const reference = practitionerParticipant?.actor?.reference;
  if (!reference) return null;
  return reference.slice('Practitioner/'.length) || null;
};

const getOrganizationId = (
  appointment: WithPlatformDefaults<fhir3.Appointment>,
): string | null => {
  const organizationParticipant = appointment.participant?.find(participant =>
    participant.actor?.reference?.startsWith('Organization/'),
  );
  const participantReference = organizationParticipant?.actor?.reference;
  if (participantReference) {
    const participantOrgId = participantReference.slice('Organization/'.length);
    if (participantOrgId) return participantOrgId;
  }

  return (
    findCoding(appointment?.meta?.tag, {
      system: 'http://lifeomic.com/fhir/fountain-life-organization',
    })?.code || null
  );
};

interface MemberAppointmentsProps {
  patientId: string;
}

export const MemberAppointments: React.FC<MemberAppointmentsProps> = ({
  patientId,
}) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const dataset = useDataset();

  const query = useInfiniteQuery({
    queryKey: ['appointments', patientId],
    queryFn: async ({ pageParam }) => {
      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/Appointment',
        {
          _tag: [
            dataset.codingToken,
            formatCodingToken({
              system: fhirSystems.source.system,
              code: fhirSystems.source.values.elationAppointment,
            }),
          ],
          patient: patientId,
          _sort: '-date',
          date: `ge1970-01-01`,
          pageSize: 25,
          next: pageParam,
        },
      );

      return {
        appointments: unwrapBundle(response.data),
        bundle: response.data,
      };
    },
    getNextPageParam: ({ bundle: lastBundle }) => {
      const nextLink = lastBundle.link?.find(link => link.relation === 'next');
      const params = new URLSearchParams(nextLink?.url);
      return params.get('next') || undefined;
    },
  });

  const appointments = useMemo(
    () => query.data?.pages.flatMap(page => page.appointments) || [],
    [query.data?.pages],
  );

  const practitionerIds = useMemo(() => {
    const ids = new Set<string>();
    appointments.forEach(appointment => {
      const practitionerId = getPractitionerParticipantId(appointment);
      if (practitionerId) ids.add(practitionerId);
    });
    return Array.from(ids);
  }, [appointments]);

  const practitionerNames = usePractitionerNames(practitionerIds);

  const organizationIds = useMemo(() => {
    const ids = new Set<string>();
    appointments.forEach(appointment => {
      const organizationId = getOrganizationId(appointment);
      if (organizationId) ids.add(organizationId);
    });
    return Array.from(ids);
  }, [appointments]);

  const organizationsQuery = useQuery({
    queryKey: ['member-appointment-organizations', organizationIds],
    queryFn: async (): Promise<Record<string, string>> => {
      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/Organization',
        {
          _tag: dataset.codingToken,
          _id: organizationIds.join(','),
          pageSize: organizationIds.length,
        },
      );
      const organizations = unwrapBundle(response.data) as Array<
        WithPlatformDefaults<fhir3.Organization>
      >;

      return organizations.reduce<Record<string, string>>(
        (acc, organization) => {
          if (organization.id) {
            acc[organization.id] = organization.name || '—';
          }
          return acc;
        },
        {},
      );
    },
    enabled: Boolean(dataset?.codingToken) && organizationIds.length > 0,
  });

  const organizationNames = useMemo(
    () => organizationsQuery.data || {},
    [organizationsQuery.data],
  );

  const tableConfig = useMemo<TableConfiguration[]>(() => {
    return [
      {
        header: {
          label: formatMessage(messages.type),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const elationReason = findCoding(appointment?.meta?.tag, {
              system: fhirSystems.appointment.elationReason.system,
            })?.code;

            return (
              <Text size="caption">
                {elationReason || appointment.description || '—'}
              </Text>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.status),
          className: styles.centerAlign,
        },
        cell: {
          className: styles.centerAlign,
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const statusMessages = {
              booked: messages.booked,
              arrived: messages.arrived,
              fulfilled: messages.fulfilled,
              cancelled: messages.cancelled,
              noshow: messages.noshow,
              pending: messages.pending,
              proposed: messages.proposed,
              'entered-in-error': messages.cancelled,
            };

            return (
              <Text size="caption">
                {formatMessage(
                  statusMessages[appointment.status] || appointment.status,
                )}
              </Text>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.startDate),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const startDate = appointment.start
              ? new Date(appointment.start).toLocaleDateString()
              : '—';
            const startTime = appointment.start
              ? new Date(appointment.start).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '';

            return (
              <Box direction="column">
                <Text size="caption">{startDate}</Text>
                {startTime && <Text size="caption">{startTime}</Text>}
              </Box>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.with),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const practitionerId = getPractitionerParticipantId(appointment);
            return (
              <Text size="caption">
                {practitionerId
                  ? practitionerNames[practitionerId] || '—'
                  : '—'}
              </Text>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.location),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const organizationId = getOrganizationId(appointment);
            const organizationName = organizationId
              ? organizationNames[organizationId] || '—'
              : '—';
            const locationTypeCode = findCoding(appointment?.meta?.tag, {
              system: fhirSystems.appointment.locationType.system,
            })?.code;

            return (
              <Box align="center" gap={1.5}>
                {locationTypeCode === 'in-person' ? (
                  <MapPin className={styles.locationIcon} />
                ) : locationTypeCode === 'virtual' ? (
                  <Video2 className={styles.locationIcon} />
                ) : null}
                <Text size="caption">{organizationName}</Text>
              </Box>
            );
          },
        },
      },
    ];
  }, [
    formatMessage,
    organizationNames,
    practitionerNames,
    styles.centerAlign,
    styles.locationIcon,
  ]);

  return (
    <div className={styles.tableContainer}>
      <InfiniteScroll
        scrollContainer="parent"
        loading={query.isFetchingNextPage}
        hasNextPage={Boolean(query.hasNextPage)}
        loadMore={() => query.fetchNextPage()}
        debounceInterval={1000}
        scrollOffset={250}
      >
        <div className={styles.tableScene}>
          <TableModule
            data={appointments}
            config={tableConfig}
            wrapCellContent
            isLoading={query.isLoading || organizationsQuery.isLoading}
          />
        </div>
      </InfiniteScroll>
    </div>
  );
};
